///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2025
//                                  RXO Inc.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of RXO Inc. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import { createSlice } from "@reduxjs/toolkit";
import { orderBy } from "lodash";

import { getPendingBids, acceptBid } from "../api/bids";
import {
  order,
  pendingBidsSortableFields,
  dateFormat,
  loadModes
} from "../helpers/types";
import formatDuration from "../helpers/formatDuration";
import _ from "lodash";
import getDeadlineDate from "../helpers/getDeadlineDate";

export const initialState = {
  bids: null,
  isFetching: false,
  searchTerm: "",
  sortField: pendingBidsSortableFields.defaultOrder,
  sortOrder: order.ascending,
  isError: false
};

const pendingBidsSlice = createSlice({
  name: "pendingBids",
  initialState,
  reducers: {
    requestPendingBids: state => ({
      bids: initialState.bids,
      isFetching: true,
      searchTerm: initialState.searchTerm,
      sortField: initialState.sortField,
      sortOrder: initialState.sortOrder,
      isError: initialState.isError
    }),
    receivePendingBidsSuccess: (state, { payload: { pendingBids, user } }) => {
      state.bids = pendingBids.map((bid, index) => ({
        ...bid,
        mode: bid.modes
          .join(", ")
          .replace(loadModes.TL.value, loadModes.TL.label)
          .replace(loadModes.LTL.value, loadModes.LTL.label)
          .replace(loadModes.IMDL.value, loadModes.IMDL.label),
        mainRepFullName: bid.mainRep.fullName,
        mainRep: {
          ...bid.mainRep
        },
        formattedPendingDuration: formatDuration(
          new Date(bid.lastStatusChangeDateUTC + "Z")
        ),
        defaultOrder: index,
        formattedDeadline: getDeadlineDate(
          { deadlineDateUTC: bid.deadlineUTC },
          user,
          Intl.DateTimeFormat().resolvedOptions().timezone,
          dateFormat.ddMMMyyy
        ),
        filtered: true
      }));
      state.isFetching = false;
      state.searchTerm = "";
    },
    receivePendingBidsFailure: state => {
      state.isFetching = false;
      state.bids = null;
      state.isError = true;
    },
    sortPendingBids: (state, { payload: { fieldName, sortReverse } }) => {
      let newSortOrder;
      let newSortField = fieldName;

      if (
        state.sortOrder === order.descending &&
        state.sortField === fieldName
      ) {
        //Clicked on column which is sorted in descending order, so default sort should be applied
        newSortOrder = order.ascending;
        newSortField = pendingBidsSortableFields.defaultOrder;
      } else if (
        state.sortOrder === order.ascending &&
        state.sortField !== pendingBidsSortableFields.defaultOrder
      ) {
        newSortOrder = order.descending;
      } else {
        newSortOrder = order.ascending;
      }

      state.sortOrder = newSortOrder;
      state.sortField = newSortField;

      if (sortReverse) {
        // Change the actual sort order in case of reverse sorting
        if (newSortOrder === order.ascending) {
          newSortOrder = order.descending;
        } else {
          newSortOrder = order.ascending;
        }
      }

      state.bids = orderBy(
        state.bids,
        [
          bid =>
            bid[newSortField] && bid[newSortField].toLowerCase
              ? bid[newSortField].toLowerCase()
              : bid[newSortField]
        ],
        [newSortOrder]
      );
    },
    filterPendingBids: (state, { payload: searchTerm }) => {
      state.searchTerm = searchTerm;
      if (state.bids) {
        const lowerCaseSearchTerm = searchTerm ? searchTerm.toLowerCase() : "";
        state.bids = state.bids.map(bid => ({
          ...bid,
          filtered:
            bid.bidName.toLowerCase().includes(lowerCaseSearchTerm) ||
            bid.mainRep.fullName.toLowerCase().includes(lowerCaseSearchTerm) ||
            (bid.shipperName
              ? bid.shipperName.toLowerCase().includes(lowerCaseSearchTerm)
              : false) ||
            bid.formattedDeadline.toLowerCase().includes(lowerCaseSearchTerm) ||
            bid.laneCount
              .toString()
              .toLowerCase()
              .includes(lowerCaseSearchTerm) ||
            bid.volume.toString().toLowerCase().includes(lowerCaseSearchTerm) ||
            bid.mode.toLowerCase().includes(lowerCaseSearchTerm) ||
            bid.formattedPendingDuration
              .toLowerCase()
              .includes(lowerCaseSearchTerm) ||
            (bid.shipperOpsGroupOffice
              ? bid.shipperOpsGroupOffice
                  .toLowerCase()
                  .includes(lowerCaseSearchTerm)
              : false)
        }));
      }
    },
    acceptPendingBidRequest: (state, { payload: bidModeId }) => {
      const bid = _.find(state.bids, { bidModeId: bidModeId });
      bid.isAcceptFetching = true;
      bid.isAcceptFailure = false;
    },
    acceptPendingBidSuccess: (state, { payload: bidModeId }) => {
      state.bids = state.bids.filter(bid => bid.bidModeId !== bidModeId);
    },
    acceptPendingBidFailure: (state, { payload: bidModeId }) => {
      const bid = _.find(state.bids, { bidModeId: bidModeId });
      bid.isAcceptFetching = false;
      bid.isAcceptFailure = true;
    },
    rejectPendingBidSuccess: (state, { payload: bidModeId }) => {
      state.bids = state.bids.filter(bid => bid.bidModeId !== bidModeId);
    },
    resetAcceptPendingBidState: (state, { payload: bidModeId }) => {
      const bid = _.find(state.bids, { bidModeId: bidModeId });
      bid.isAcceptFetching = false;
      bid.isAcceptFailure = false;
    }
  }
});

export const {
  requestPendingBids,
  receivePendingBidsSuccess,
  receivePendingBidsFailure,
  sortPendingBids,
  filterPendingBids,
  acceptPendingBidRequest,
  acceptPendingBidSuccess,
  acceptPendingBidFailure,
  rejectPendingBidSuccess,
  resetAcceptPendingBidState
} = pendingBidsSlice.actions;

export const pendingBidsReducer = pendingBidsSlice.reducer;

export const fetchPendingBids = () => async (dispatch, getState) => {
  const { user } = getState().user;
  let pendingBids;
  dispatch(requestPendingBids());

  try {
    pendingBids = (await getPendingBids(user.userRoleForManagementUser)).data;
    dispatch(
      receivePendingBidsSuccess({ pendingBids, user: getState().user.user })
    );
  } catch {
    dispatch(receivePendingBidsFailure());
  }
};

export const acceptPendingBid =
  (bidId, bidModeId) => async (dispatch, getState) => {
    const { user } = getState().user;
    dispatch(acceptPendingBidRequest(bidModeId));
    try {
      await acceptBid(bidId, bidModeId, user.userRoleForManagementUser);
      dispatch(acceptPendingBidSuccess(bidModeId));
    } catch {
      dispatch(acceptPendingBidFailure(bidModeId));
    }
  };
